<template>
  <base-layout>
    <template v-slot:header>
      <nav class="mb-2">
        <router-link
          to="/services"
          class="flex items-center text-sm leading-5 font-medium text-gray-500 hover:text-gray-700"
        >
          <svg class="flex-shrink-0 -ml-1 mr-1 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
          All services
        </router-link>
      </nav>
      <div class="flex items-center gap-3">
        <img
          v-if="category && category.imgName"
          class="w-12 h-12 object-contain"
          :src="category.imgName"
          :alt="category.catName"
        >
        <div>
          <h1 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
            {{ category ? category.catName : 'Service category' }}
          </h1>
          <p v-if="category" class="mt-1 text-sm text-gray-600">
            {{ category.companies.length }}
            {{ category.companies.length === 1 ? 'provider' : 'providers' }} listed
          </p>
        </div>
      </div>
    </template>

    <template v-slot:main>
      <div v-if="isLoading" class="text-gray-500 py-8">Loading…</div>
      <div v-else-if="!category" class="py-8">
        <p class="text-gray-700 mb-4">This service category was not found.</p>
        <router-link to="/services" class="text-red-600 hover:underline">Back to services directory</router-link>
      </div>
      <div v-else>
        <p class="text-base text-gray-700 leading-relaxed max-w-3xl mb-8">
          Browse {{ category.catName.toLowerCase() }} providers listed on The Broker Site.
          These companies offer services to IT traders, refurbishers, wholesalers, and resellers.
          Click a company to view their full profile and contact details.
        </p>

        <div class="bg-white shadow overflow-hidden sm:rounded-md">
          <ul class="divide-y divide-gray-200">
            <li v-for="company in category.companies" :key="company.id">
              <!-- Landing page: navigate to company SEO page (also crawlable) -->
              <router-link
                :to="company.companyHref"
                class="block hover:bg-gray-50 px-4 py-4 sm:px-6"
              >
                <div class="flex items-center justify-between gap-4">
                  <div class="min-w-0">
                    <p class="text-base font-semibold text-gray-900 truncate">{{ company.name }}</p>
                    <p class="mt-1 text-sm text-gray-500">{{ processCountryName(company.compCountry) }}</p>
                  </div>
                  <svg class="h-5 w-5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
                  </svg>
                </div>
              </router-link>
            </li>
          </ul>
        </div>

        <p class="mt-8 text-center text-sm text-gray-600">
          To be listed in this category, please contact
          <a
            :href="`mailto:rhody@thebrokersite.com?Subject=TBS Service listing in category ${category.catName}`"
            class="text-red-600 hover:underline"
          >rhody@thebrokersite.com</a>
        </p>
      </div>
    </template>
  </base-layout>
</template>

<script>
import axios from 'axios'
import BaseLayout from '@/components/BaseLayout.vue'
import { groupServicesByCategory } from '@/utils/serviceDirectory'
import { useSchemaOrg } from '@/composables/useSchema.org.js'

export default {
  name: 'ServiceCategory',
  components: {
    BaseLayout,
  },
  data: () => ({
    isLoading: true,
    category: null,
  }),
  async created() {
    await this.loadCategory()
  },
  watch: {
    '$route.params.categorySlug'() {
      this.loadCategory()
    },
  },
  methods: {
    async loadCategory() {
      this.isLoading = true
      this.category = null
      try {
        const { data } = await axios.get('/api/services')
        const groups = groupServicesByCategory(data.payload || [])
        const slug = (this.$route.params.categorySlug || '').toLowerCase()
        this.category = groups.find(g => g.categorySlug === slug) || null

        if (this.category) {
          this.applyMeta()
          this.addSchema()
        } else {
          document.title = 'Service category not found - The Broker Site'
        }
      } finally {
        this.isLoading = false
      }
    },
    applyMeta() {
      const name = this.category.catName
      const slug = this.category.categorySlug
      document.title = `${name} Services - The Broker Site`

      this.upsertMeta('description', `Find ${name} providers on The Broker Site. Browse listed companies offering ${name.toLowerCase()} to the circular IT industry.`)
      this.upsertLink('canonical', `https://www.thebrokersite.com/services/${slug}`)
    },
    upsertMeta(name, content) {
      let tag = document.querySelector(`meta[name="${name}"]`)
      if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute('name', name)
        document.head.appendChild(tag)
      }
      tag.setAttribute('content', content)
    },
    upsertLink(rel, href) {
      let tag = document.querySelector(`link[rel="${rel}"]`)
      if (!tag) {
        tag = document.createElement('link')
        tag.setAttribute('rel', rel)
        document.head.appendChild(tag)
      }
      tag.setAttribute('href', href)
    },
    addSchema() {
      useSchemaOrg({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `${this.category.catName} Services - The Broker Site`,
        description: `Directory of ${this.category.catName} providers`,
        url: `https://www.thebrokersite.com/services/${this.category.categorySlug}`,
        itemListElement: this.category.companies.map((company, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Organization',
            name: company.name,
            url: `https://www.thebrokersite.com${company.companyHref}`,
            address: {
              '@type': 'PostalAddress',
              addressCountry: company.compCountry,
            },
          },
        })),
      })
    },
    processCountryName(strCountry) {
      switch (strCountry) {
        case 'United Arab Emirates':
          return 'UAE'
        case 'United States':
          return 'USA'
        case 'United Kingdom':
          return 'UK'
        default:
          return strCountry
      }
    },
  },
}
</script>
